import { OnboardingFormData } from '../store/onboardingStore';

// ============================================
// VALIDATION RULES
// ============================================

export interface ValidationError {
  field: string;
  message: string;
}

export const validationRules = {
  // Business Information
  businessName: {
    validate: (value: string) =>
      !!value && value.trim().length >= 2 && value.trim().length <= 100,
    message: 'Business name must be 2-100 characters',
  },
  llcName: {
    validate: (value: string) =>
      !!value && value.trim().length >= 2 && value.trim().length <= 100,
    message: 'LLC name must be 2-100 characters',
  },
  businessType: {
    validate: (value: string) =>
      !!value && ['sole_proprietor', 'partnership', 'llc', 'corporation', 'other'].includes(value),
    message: 'Please select a valid business type',
  },
  taxId: {
    validate: (value: string) => /^\d{2}-\d{7}$/.test(value.replace(/[^\d-]/g, '')),
    message: 'Tax ID must be in format XX-XXXXXXX',
  },
  businessLicense: {
    validate: (value: string) => !!value && value.trim().length >= 2,
    message: 'Business license is required',
  },

  // Contact Information
  contactName: {
    validate: (value: string) =>
      !!value && value.trim().length >= 2 && value.trim().length <= 100,
    message: 'Contact name must be 2-100 characters',
  },
  contactEmail: {
    validate: (value: string) =>
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value),
    message: 'Please enter a valid email address',
  },
  contactPhone: {
    validate: (value: string) => {
      const cleaned = value.replace(/\D/g, '');
      return cleaned.length === 10;
    },
    message: 'Please enter a valid 10-digit phone number',
  },

  // Address Information
  addressLine1: {
    validate: (value: string) =>
      !!value && value.trim().length >= 5 && value.trim().length <= 255,
    message: 'Address must be 5-255 characters',
  },
  city: {
    validate: (value: string) =>
      !!value && value.trim().length >= 2 && value.trim().length <= 50,
    message: 'City must be 2-50 characters',
  },
  state: {
    validate: (value: string) =>
      !!value && /^[A-Z]{2}$/.test(value),
    message: 'Please enter a valid 2-letter state code',
  },
  zip: {
    validate: (value: string) =>
      /^\d{5}(-\d{4})?$/.test(value),
    message: 'Please enter a valid ZIP code (XXXXX or XXXXX-XXXX)',
  },
  country: {
    validate: (value: string) => !!value && value.trim().length >= 2,
    message: 'Please enter a valid country',
  },

  // Banking Information
  bankName: {
    validate: (value: string) =>
      !!value && value.trim().length >= 2 && value.trim().length <= 100,
    message: 'Bank name must be 2-100 characters',
  },
  accountType: {
    validate: (value: string) =>
      !!value && ['checking', 'savings'].includes(value),
    message: 'Please select a valid account type',
  },
  accountNumber: {
    validate: (value: string) => {
      const cleaned = value.replace(/\D/g, '');
      return cleaned.length >= 8 && cleaned.length <= 17;
    },
    message: 'Account number must be 8-17 digits',
  },
  routingNumber: {
    validate: (value: string) => {
      const cleaned = value.replace(/\D/g, '');
      return cleaned.length === 9;
    },
    message: 'Routing number must be 9 digits',
  },

  // Agreement
  agreeToTerms: {
    validate: (value: boolean) => value === true,
    message: 'You must agree to the terms and conditions',
  },
  signatureData: {
    validate: (value: string) => !!value && value.length > 0,
    message: 'Please provide a signature',
  },
};

// ============================================
// VALIDATION FUNCTIONS
// ============================================

export function validateField(
  field: string,
  value: any
): { isValid: boolean; error?: string } {
  const rule = validationRules[field as keyof typeof validationRules];

  if (!rule) {
    return { isValid: true };
  }

  const isValid = (rule.validate as any)(value);
  return {
    isValid,
    error: !isValid ? rule.message : undefined,
  };
}

export function validateStep(
  step: number,
  formData: OnboardingFormData,
  uploadedFilesCount?: number
): Map<string, string> {
  const errors = new Map<string, string>();

  const stepFields: Record<number, (keyof OnboardingFormData)[]> = {
    1: ['businessName', 'llcName', 'addressLine1', 'city', 'state', 'zip', 'country'],
    2: ['contactName', 'contactEmail', 'contactPhone'],
    3: [], // Documents validation is handled separately
    4: ['bankName', 'accountType', 'accountNumber', 'routingNumber'],
    5: ['agreeToTerms', 'signatureData'],
  };

  const fieldsToValidate = stepFields[step] || [];

  for (const field of fieldsToValidate) {
    const validation = validateField(field, formData[field]);
    if (!validation.isValid && validation.error) {
      errors.set(field, validation.error);
    }
  }

  // Special validation for Step 3 (Documents)
  if (step === 3) {
    const requiredDocumentCount = 3; // Tax ID, Federal ID, Tobacco License
    if ((uploadedFilesCount || 0) < requiredDocumentCount) {
      errors.set('documents', `Please upload all ${requiredDocumentCount} required documents`);
    }
  }

  return errors;
}

export function isFormValid(formData: OnboardingFormData): boolean {
  // Validate all steps
  for (let step = 1; step <= 5; step++) {
    const errors = validateStep(step, formData);
    if (errors.size > 0) {
      return false;
    }
  }
  return true;
}

// ============================================
// FORMATTING UTILITIES
// ============================================

export function formatPhoneNumber(value: string): string {
  const cleaned = value.replace(/\D/g, '');
  if (cleaned.length === 0) return '';
  if (cleaned.length <= 3) return cleaned;
  if (cleaned.length <= 6) return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
  return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
}

export function formatTaxId(value: string): string {
  const cleaned = value.replace(/\D/g, '');
  if (cleaned.length === 0) return '';
  if (cleaned.length <= 2) return cleaned;
  return `${cleaned.slice(0, 2)}-${cleaned.slice(2, 9)}`;
}

export function formatRoutingNumber(value: string): string {
  const cleaned = value.replace(/\D/g, '');
  return cleaned.slice(0, 9);
}

export function maskAccountNumber(value: string): string {
  const cleaned = value.replace(/\D/g, '');
  if (cleaned.length <= 4) return cleaned;
  const last4 = cleaned.slice(-4);
  const masked = '*'.repeat(cleaned.length - 4);
  return `${masked}${last4}`;
}

export function maskTaxId(value: string): string {
  if (!value) return '';
  const parts = value.split('-');
  if (parts.length !== 2) return '**-*******';
  return `${parts[0]}-${parts[1].slice(0, 1)}${parts[1].slice(1).replace(/\d/g, '*')}`;
}

// ============================================
// FILE VALIDATION
// ============================================

export interface FileValidationResult {
  isValid: boolean;
  error?: string;
}

export const fileValidationConfig = {
  maxSize: 10 * 1024 * 1024, // 10MB
  allowedTypes: ['application/pdf', 'image/jpeg', 'image/png'],
  allowedExtensions: ['.pdf', '.jpg', '.jpeg', '.png'],
};

export function validateFile(file: File): FileValidationResult {
  // Check file size
  if (file.size > fileValidationConfig.maxSize) {
    return {
      isValid: false,
      error: `File is too large. Maximum size is 10MB. (${(file.size / 1024 / 1024).toFixed(2)}MB)`,
    };
  }

  // Check file type
  if (!fileValidationConfig.allowedTypes.includes(file.type)) {
    return {
      isValid: false,
      error: 'Invalid file type. Only PDF, JPG, and PNG files are allowed.',
    };
  }

  // Check file extension
  const fileName = file.name.toLowerCase();
  const hasValidExtension = fileValidationConfig.allowedExtensions.some((ext) =>
    fileName.endsWith(ext)
  );

  if (!hasValidExtension) {
    return {
      isValid: false,
      error: 'Invalid file extension. Only .pdf, .jpg, .jpeg, and .png are allowed.',
    };
  }

  return { isValid: true };
}
